import * as THREE from 'three';
import {
  createBirchClusterModel,
  createFallenLogModel,
  createRangerHutModel,
  createSnowDivingFoxModel,
  createSnowmanModel,
  createSnowshoeHareModel,
} from './lanternwoodModels';
import {
  createCrystalFanModel,
  createFrozenIcefallModel,
  createIceArchModel,
  createIceboundSurveySledModel,
  createMittenSkiddingErmineModel,
} from './iceGardenModels';
import {
  createAvalancheFenceModel,
  createKrummholzModel,
  createPowderBathingPtarmiganPairModel,
  createRavenCacheModel,
  createRibbonCairnModel,
  createWeatherMastModel,
} from './timberlineModels';

export const MODEL_BIOMES = ['lanternwood', 'ice-garden', 'timberline'] as const;
export type ModelBiome = (typeof MODEL_BIOMES)[number];

export interface EnvironmentModelDefinition {
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly biome: ModelBiome;
  readonly create: () => THREE.Group;
}

export const MODEL_CATALOG = [
  {
    id: 'ranger-hut',
    name: 'Lanternwood Ranger Hut',
    tagline: 'A snow-loaded log shelter whose amber windows warm the glade.',
    biome: 'lanternwood',
    create: createRangerHutModel,
  },
  {
    id: 'birch-cluster',
    name: 'Silver Birch Cluster',
    tagline: 'A leaning family of pale, scarred trunks holding pockets of fresh snow.',
    biome: 'lanternwood',
    create: createBirchClusterModel,
  },
  {
    id: 'fallen-log',
    name: 'Snow-Covered Nurse Log',
    tagline: 'A broken old trunk sheltering a stubborn new pine beneath its snow pillows.',
    biome: 'lanternwood',
    create: createFallenLogModel,
  },
  {
    id: 'snowman',
    name: 'Scarfed Trail Snowman',
    tagline: 'A cheerful trail companion with twig arms and a wind-tossed coral scarf.',
    biome: 'lanternwood',
    create: createSnowmanModel,
  },
  {
    id: 'snowshoe-hare',
    name: 'Listening Snowshoe Hare',
    tagline: 'A winter-white hare frozen mid-listen, ears high above the powder.',
    biome: 'lanternwood',
    create: createSnowshoeHareModel,
  },
  {
    id: 'snow-diving-fox',
    name: 'Powder-Diving Red Fox',
    tagline: 'A russet hunter vanishing nose-first into powder while its plume and hind paws hang in the air.',
    biome: 'lanternwood',
    create: createSnowDivingFoxModel,
  },
  {
    id: 'ice-arch',
    name: 'Blue Ice Passage',
    tagline: 'A monumental, skiable portal assembled from weather-cut glacial blocks.',
    biome: 'ice-garden',
    create: createIceArchModel,
  },
  {
    id: 'crystal-fan',
    name: 'Glacial Crystal Fan',
    tagline: 'A radiant spray of angular blue crystals erupting through a wind drift.',
    biome: 'ice-garden',
    create: createCrystalFanModel,
  },
  {
    id: 'frozen-icefall',
    name: 'Frozen Hanging Icefall',
    tagline: 'A dark cliff draped in long, faceted curtains of luminous blue ice.',
    biome: 'ice-garden',
    create: createFrozenIcefallModel,
  },
  {
    id: 'icebound-survey-sled',
    name: 'Icebound Survey Sled',
    tagline: 'An abandoned glacier-mapping pulk slowly captured by wind-polished blue ice.',
    biome: 'ice-garden',
    create: createIceboundSurveySledModel,
  },
  {
    id: 'mitten-skidding-ermine',
    name: 'Mitten-Skidding Ermine',
    tagline: 'A winter-white thief belly-slides across the glacier with one coral survey mitten.',
    biome: 'ice-garden',
    create: createMittenSkiddingErmineModel,
  },
  {
    id: 'krummholz',
    name: 'Wind-Sculpted Krummholz',
    tagline: 'An alpine pine pressed permanently downhill by seasons of hard wind.',
    biome: 'timberline',
    create: createKrummholzModel,
  },
  {
    id: 'ribbon-cairn',
    name: 'Ribbon-Topped Trail Cairn',
    tagline: 'A careful stone stack flying scraps of warm cloth in the cold air.',
    biome: 'timberline',
    create: createRibbonCairnModel,
  },
  {
    id: 'avalanche-fence',
    name: 'Timber Avalanche Fence',
    tagline: 'A braced line of weathered rails built to hold the upper snowpack.',
    biome: 'timberline',
    create: createAvalancheFenceModel,
  },
  {
    id: 'weather-mast',
    name: 'Timberline Weather Mast',
    tagline: 'A tripod station watching the ridge with cups, vane, and instrument box.',
    biome: 'timberline',
    create: createWeatherMastModel,
  },
  {
    id: 'raven-cache',
    name: 'Raven-Raided Ridge Pack',
    tagline: 'A bold alpine raven claims a snowbound pack and its brightest woolen prize.',
    biome: 'timberline',
    create: createRavenCacheModel,
  },
  {
    id: 'powder-bathing-ptarmigan-pair',
    name: 'Powder-Bathing Ptarmigan Pair',
    tagline: 'One winter bird erupts through powder while its companion keeps watch across the ridge.',
    biome: 'timberline',
    create: createPowderBathingPtarmiganPairModel,
  },
] as const satisfies readonly EnvironmentModelDefinition[];

export type EnvironmentModelId = (typeof MODEL_CATALOG)[number]['id'];

export function getEnvironmentModelDefinition(id: string): EnvironmentModelDefinition | undefined {
  return MODEL_CATALOG.find((definition) => definition.id === id);
}

export function createEnvironmentModel(id: string): THREE.Group {
  const definition = getEnvironmentModelDefinition(id);
  if (!definition) throw new Error(`Unknown environment model: "${id}".`);
  return definition.create();
}
